import { QueryClient } from '@tanstack/react-query';

/**
 * TanStack Query defaults tuned for a financial dashboard.
 *
 * - Short `staleTime` so wallet / commission / withdrawal figures never
 *   feel stale after an admin action. Individual queries can override
 *   (e.g. tree children use 60s cache since they're read-heavy).
 * - `refetchOnWindowFocus: true` so switching tabs pulls fresh money.
 * - Retry once with exponential-ish backoff on GETs; mutations never retry
 *   because most of ours are idempotent by reference number but retrying
 *   any that aren't would be a foot-gun.
 * - No global `gcTime` bump — the default 5m keeps memory bounded for
 *   long-lived tree explorations.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 15_000,
      gcTime: 5 * 60_000,
      retry: (failureCount, err) => {
        // Never retry 4xx (auth/validation) — surface immediately.
        const status = (err as { status?: number } | undefined)?.status;
        if (status && status >= 400 && status < 500) return false;
        return failureCount < 2;
      },
      retryDelay: (attempt) => Math.min(2 ** attempt * 250, 4_000),
      refetchOnWindowFocus: true,
      refetchOnReconnect: 'always',
    },
    mutations: {
      retry: 0,
    },
  },
});
